/**
 * Pure graph computation for deleting multiple nodes from a workflow and
 * reconnecting the surviving chain segments.
 *
 * Shared by the server (`apps/api` bulk-delete route, which persists the ops)
 * and the client (`apps/web` flow designer's suggest-changes mode, which applies
 * the reconnect edges to local canvas state without hitting the server). Keeping
 * the algorithm in one place guarantees the suggest-mode optimistic graph
 * matches what the server would have produced.
 *
 * For each contiguous run of deleted nodes, walk backward to the first surviving
 * source and forward to the first surviving target, then emit one reconnection
 * edge between them.
 *
 * @module workflow-diff/bulk-delete
 */

/** Minimal edge shape (canonical source/target naming). */
export interface BulkDeleteEdge {
  id: string;
  sourceNodeId: string;
  targetNodeId: string;
}

export interface BulkDeleteReconnectOps {
  /** New edges that bridge the gaps left by deleted nodes. */
  reconnectEdges: Array<{ sourceNodeId: string; targetNodeId: string }>;
  /** Ids of edges touching a deleted node (to remove). */
  removedEdgeIds: string[];
}

/**
 * Compute the edge mutations for a bulk node delete.
 *
 * Pure — no I/O. The caller decides how to apply the result (DB writes on the
 * server, React Flow state updates on the client). Node order is derived from
 * edges everywhere, so this no longer re-indexes any `orderIndex`.
 */
export function computeBulkDeleteReconnectOps(params: {
  edges: BulkDeleteEdge[];
  deleteNodeIds: readonly string[];
  reconnect: boolean;
}): BulkDeleteReconnectOps {
  const { edges, deleteNodeIds, reconnect } = params;
  const deleteSet = new Set(deleteNodeIds);

  const reconnectEdges: Array<{ sourceNodeId: string; targetNodeId: string }> = [];
  if (reconnect) {
    // Build adjacency from current edges.
    const outgoing = new Map<string, string>();
    const incoming = new Map<string, string>();
    for (const e of edges) {
      outgoing.set(e.sourceNodeId, e.targetNodeId);
      incoming.set(e.targetNodeId, e.sourceNodeId);
    }

    const seen = new Set<string>();
    for (const nodeId of deleteSet) {
      // Walk backward through deleted nodes to find the surviving source.
      let src: string | undefined = incoming.get(nodeId);
      while (src && deleteSet.has(src)) {
        src = incoming.get(src);
      }
      if (!src || seen.has(src)) continue;

      // Walk forward through deleted nodes to find the surviving target.
      let tgt: string | undefined = outgoing.get(nodeId);
      while (tgt && deleteSet.has(tgt)) {
        tgt = outgoing.get(tgt);
      }
      if (!tgt) continue;

      seen.add(src);
      reconnectEdges.push({ sourceNodeId: src, targetNodeId: tgt });
    }
  }

  const removedEdgeIds = edges
    .filter((e) => deleteSet.has(e.sourceNodeId) || deleteSet.has(e.targetNodeId))
    .map((e) => e.id);

  return { reconnectEdges, removedEdgeIds };
}
